import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  documentId,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../../firebase";
import UserAvatar from "../../../components/ui/UserAvatar";
import UserTopNav from "../../../components/layout/UserTopNav";
import NoticeStack, { type NoticeItem, type NoticeTone } from "../../../components/ui/NoticeStack";

type BookingStatus = "booked" | "checked_in" | "authorised_absence" | "dip";

type AttendanceStatus = "none" | "checked_in" | "dip";

type BookingRow = {
  userId: string;
  userName?: string;
  name?: string;
  email?: string;
  photoURL?: string;
  guestName?: string | null;
  guestEmail?: string | null;
  guestDateOfBirth?: string | null;
  guestPhone?: string | null;
  bookingSource?: "membership" | "payg" | "admin";
  guestTag?: boolean;

  // legacy / transitional fields from backend
  status?: string;
  attendanceStatus?: AttendanceStatus;
  attended?: boolean;
  checkedInAt?: any;
  addedByAdmin?: boolean;
};

type RosterResponse = {
  classId: string;
  total: number;
  checkedInCount: number;
  attendees: BookingRow[];
};

type UserProfile = {
  name?: string;
  email?: string;
  photoURL?: string;
};

type GymUser = {
  id: string;
  name?: string;
  email?: string;
  photoURL?: string;
  role?: string;
  approvalStatus?: "approved" | "pending";
};

type WaitlistRow = {
  userId: string;
  userName?: string;
  email?: string;
  photoURL?: string;
  createdAt?: { toDate?: () => Date } | null;
  status: "waiting" | "promoted" | "left" | "expired";
};

function formatJoinedTime(value?: { toDate?: () => Date } | null) {
  const date = value?.toDate?.();
  if (!date) return "Just joined";

  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function fetchUserProfiles(uids: string[]) {
  const map = new Map<string, UserProfile>();
  const unique = Array.from(new Set(uids.filter(Boolean)));
  if (!unique.length) return map;

  for (let i = 0; i < unique.length; i += 10) {
    const chunk = unique.slice(i, i + 10);
    const q = query(collection(db, "users"), where(documentId(), "in", chunk));
    const snap = await getDocs(q);
    snap.forEach((d) => map.set(d.id, d.data() as UserProfile));
  }

  return map;
}

function normalizeStatus(r: BookingRow): BookingStatus {
  if (r.attendanceStatus === "dip") return "dip";
  if (r.attendanceStatus === "checked_in") return "checked_in";
  if (r.attended === true) return "checked_in";
  return "booked";
}

function StatusPill({ status }: { status: BookingStatus }) {
  const base =
    "text-xs uppercase tracking-widest px-3 py-1 rounded-full border inline-flex items-center gap-2";

  if (status === "checked_in") {
    return <span className={`${base} border-emerald-500/60 text-emerald-200`}>Checked in</span>;
  }

  if (status === "dip") {
    return <span className={`${base} border-red-500/50 text-red-200`}>Dip</span>;
  }

  if (status === "authorised_absence") {
    return <span className={`${base} border-sky-500/50 text-sky-200`}>Auth abs</span>;
  }

  return <span className={`${base} border-white/15 text-white/60`}>Booked</span>;
}

export default function ClassRoster() {
  const { classId } = useParams<{ classId: string }>();
  const auth = getAuth();

  const [classTitle, setClassTitle] = useState("Class");
  const [classMeta, setClassMeta] = useState("");
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [waitlistRows, setWaitlistRows] = useState<WaitlistRow[]>([]);
  const [serverCounts, setServerCounts] = useState<{ total: number; checkedIn: number }>({
    total: 0,
    checkedIn: 0,
  });

  const [loadingRoster, setLoadingRoster] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const [allUsers, setAllUsers] = useState<GymUser[]>([]);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberError, setAddMemberError] = useState("");
  const [busyWaitlistUserId, setBusyWaitlistUserId] = useState<string | null>(null);
  const [notices, setNotices] = useState<NoticeItem[]>([]);

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([id]) => id),
    [selected]
  );

  const toggleSelected = useCallback((uid: string) => {
    setSelected((prev) => ({ ...prev, [uid]: !prev[uid] }));
  }, []);

  const clearSelected = useCallback(() => setSelected({}), []);

  const pushNotice = useCallback((message: string, tone: NoticeTone = "info") => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setNotices((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => {
      setNotices((current) => current.filter((notice) => notice.id !== id));
    }, 4200);
  }, []);

  const dismissNotice = useCallback((id: number) => {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }, []);

  const loadClassSummary = useCallback(async () => {
    if (!classId) return;

    const snap = await getDoc(doc(db, "classes", classId));
    if (!snap.exists()) return;

    const d: any = snap.data();
    setClassTitle(d.title || "Class");
    setWaitlistCount(Number(d.waitlistCount ?? 0));

    const start = d.startTime?.toDate?.();
    const end = d.endTime?.toDate?.();

    const time =
      start && end
        ? `${start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}–${end.toLocaleTimeString(
            "en-GB",
            { hour: "2-digit", minute: "2-digit" }
          )}`
        : "";

    const date = start
      ? start.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })
      : "";

    setClassMeta([date, time, d.location].filter(Boolean).join(" • "));
  }, [classId]);

  useEffect(() => {
    if (!classId) return;

    (async () => {
      await loadClassSummary();
    })();
  }, [classId, loadClassSummary]);

  useEffect(() => {
    if (!classId) {
      setWaitlistRows([]);
      return;
    }

    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "waitlists"),
            where("classId", "==", classId),
            where("status", "==", "waiting"),
            orderBy("createdAt", "asc")
          )
        );

        const baseRows = snap.docs.map((d) => d.data() as WaitlistRow);
        const profiles = await fetchUserProfiles(baseRows.map((row) => row.userId));

        setWaitlistRows(
          baseRows.map((row) => {
            const profile = profiles.get(row.userId);
            return {
              ...row,
              userName: profile?.name ?? row.userName ?? "Member",
              photoURL: profile?.photoURL ?? row.photoURL,
              email: profile?.email ?? row.email ?? "",
            };
          })
        );
      } catch (err) {
        console.error("Failed to load waitlist:", err);
        setWaitlistRows([]);
      }
    })();
  }, [classId, waitlistCount]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        const users: GymUser[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<GymUser, "id">),
        })).filter((user) => user.approvalStatus !== "pending");

        users.sort((a, b) => (a.name ?? a.email ?? "").localeCompare(b.name ?? b.email ?? ""));
        setAllUsers(users);
      } catch (err) {
        console.error("Failed to load users for add-member picker:", err);
      }
    })();
  }, []);

  const loadRoster = useCallback(async () => {
    if (!classId) return;

    try {
      setLoadingRoster(true);

      const functions = getFunctions(undefined, "europe-west1");
      const getRoster = httpsCallable<{ classId: string }, RosterResponse>(functions, "getClassRoster");

      const res = await getRoster({ classId });
      const data = res.data;
      const attendees = data.attendees || [];

      const profiles = await fetchUserProfiles(attendees.map((a) => a.userId));

      const enriched: BookingRow[] = attendees.map((r) => {
        const u = profiles.get(r.userId);
        const isGuest = r.bookingSource === "payg" || r.guestTag === true;

        return {
          ...r,
          name: isGuest ? r.guestName ?? r.userName ?? "Guest" : u?.name ?? r.name ?? r.userName ?? "Member",
          email: isGuest ? r.guestEmail ?? r.email ?? "" : u?.email ?? r.email ?? "",
          photoURL: u?.photoURL ?? r.photoURL,
          attendanceStatus:
            r.attendanceStatus ??
            ((r as any).status === "dip" ? "dip" : r.attended === true ? "checked_in" : "none"),
        };
      });

      setRows(enriched);

      const localCheckedIn = enriched.filter((x) => normalizeStatus(x) === "checked_in").length;

      setServerCounts({
        total: data.total ?? enriched.length,
        checkedIn: data.checkedInCount ?? localCheckedIn,
      });
    } catch (err: any) {
      console.error("Roster error:", err);
      pushNotice(err?.message || "Failed to load roster.", "error");
      setRows([]);
      setServerCounts({ total: 0, checkedIn: 0 });
    } finally {
      setLoadingRoster(false);
    }
  }, [classId, pushNotice]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const localCheckedInCount = useMemo(
    () => rows.filter((r) => normalizeStatus(r) === "checked_in").length,
    [rows]
  );

  const checkedInShown = serverCounts.checkedIn || localCheckedInCount;
  const totalShown = serverCounts.total || rows.length;

  const sortedRows = useMemo(() => {
    const rank = (s: BookingStatus) => {
      if (s === "checked_in") return 0;
      if (s === "booked") return 1;
      if (s === "dip") return 2;
      return 3;
    };

    const copy = [...rows];
    copy.sort((a, b) => {
      const as = normalizeStatus(a);
      const bs = normalizeStatus(b);
      const ra = rank(as);
      const rb = rank(bs);
      if (ra !== rb) return ra - rb;
      return (a.name ?? "").localeCompare(b.name ?? "");
    });
    return copy;
  }, [rows]);

  const addableUsers = useMemo(() => {
    const activeIds = new Set(
      rows
        .filter((r) => {
          const status = normalizeStatus(r);
          return status === "booked" || status === "checked_in";
        })
        .map((r) => r.userId)
    );

    return allUsers.filter((u) => !activeIds.has(u.id));
  }, [allUsers, rows]);

  async function setStatus(userId: string, next: BookingStatus) {
    const user = auth.currentUser;
    if (!user) {
      pushNotice("Log in first.", "error");
      return;
    }
    if (!classId) return;

    try {
      setBusyUserId(userId);

      if (next === "checked_in" || next === "booked") {
        const attended = next === "checked_in";
        const mod = await import("../services/checkin");
        await mod.checkInBooking({ classId, userId, attended });
      } else {
        const functions = getFunctions(undefined, "europe-west1");
        const mark = httpsCallable(functions, "markBookingStatus");
        await mark({ classId, userId, status: next });
      }

      await loadRoster();
    } catch (e: any) {
      console.error("Status error:", e);
      alert(
        `code: ${e?.code ?? "?"}\nmessage: ${e?.message ?? "Update failed"}\ndetails: ${JSON.stringify(
          e?.details ?? {},
          null,
          2
        )}`
      );
    } finally {
      setBusyUserId(null);
    }
  }

  async function bulkSetStatus(next: BookingStatus, idsArg?: string[]) {
    const user = auth.currentUser;
    if (!user) return alert("Log in first.");
    if (!classId) return;

    const ids = idsArg ?? selectedIds;
    if (!ids.length) return;

    try {
      setBulkBusy(true);

      if (next === "checked_in" || next === "booked") {
        const attended = next === "checked_in";
        const mod = await import("../services/checkin");

        const results = await Promise.allSettled(
          ids.map((uid) => mod.checkInBooking({ classId, userId: uid, attended }))
        );

        const failed = results.filter((x) => x.status === "rejected").length;
        await loadRoster();

        if (failed > 0) {
          pushNotice(`Updated ${ids.length - failed}/${ids.length}. ${failed} failed — try again or refresh.`, "error");
        } else {
          pushNotice(`Updated ${ids.length} attendee${ids.length === 1 ? "" : "s"}.`, "success");
        }
      } else {
        const functions = getFunctions(undefined, "europe-west1");
        const mark = httpsCallable(functions, "markBookingStatus");

        const results = await Promise.allSettled(ids.map((uid) => mark({ classId, userId: uid, status: next })));
        const failed = results.filter((x) => x.status === "rejected").length;

        await loadRoster();

        if (failed > 0) {
          pushNotice(`Updated ${ids.length - failed}/${ids.length}. ${failed} failed — try again or refresh.`, "error");
        } else {
          pushNotice(`Updated ${ids.length} attendee${ids.length === 1 ? "" : "s"}.`, "success");
        }
      }

      clearSelected();
      setSelectMode(false);
    } catch (e: any) {
      console.error("Bulk update error:", e);
      pushNotice(e?.message ?? "Bulk update failed", "error");
    } finally {
      setBulkBusy(false);
    }
  }

  async function checkInAll() {
    const ids = rows
      .filter((r) => normalizeStatus(r) !== "checked_in")
      .map((r) => r.userId);

    if (!ids.length) return;
    await bulkSetStatus("checked_in", ids);
  }

  async function uncheckAll() {
    const ids = rows
      .filter((r) => normalizeStatus(r) === "checked_in")
      .map((r) => r.userId);

    if (!ids.length) return;
    await bulkSetStatus("booked", ids);
  }

  async function handleAdminAddMember() {
    if (!classId) return;

    const selectedUser =
      addableUsers.find((u) => u.id === selectedUserId) ??
      allUsers.find((u) => u.id === selectedUserId);

    if (!selectedUser) {
      setAddMemberError("Please select a member.");
      return;
    }

    try {
      setAddingMember(true);
      setAddMemberError("");

      const functions = getFunctions(undefined, "europe-west1");
      const adminAddBooking = httpsCallable(functions, "adminAddBooking");

      await adminAddBooking({
        classId,
        userId: selectedUser.id,
      });

      setShowAddMemberModal(false);
      setSelectedUserId("");
      await loadRoster();
      pushNotice("Member added to class.", "success");
    } catch (err: any) {
      console.error("Admin add member error:", err);
      setAddMemberError(err?.message ?? "Failed to add member.");
    } finally {
      setAddingMember(false);
    }
  }

  async function handlePromoteWaitlist(userId: string) {
    if (!classId) return;

    try {
      setBusyWaitlistUserId(userId);
      const functions = getFunctions(undefined, "europe-west1");
      const promote = httpsCallable(functions, "adminPromoteWaitlist");
      await promote({ classId, userId });
      await Promise.all([loadRoster(), loadClassSummary()]);
      pushNotice("Member promoted from waitlist.", "success");
    } catch (err: any) {
      console.error("Promote waitlist error:", err);
      pushNotice(err?.message ?? "Failed to promote member.", "error");
    } finally {
      setBusyWaitlistUserId(null);
    }
  }

  async function handleRemoveWaitlist(userId: string) {
    if (!classId) return;

    try {
      setBusyWaitlistUserId(userId);
      const functions = getFunctions(undefined, "europe-west1");
      const remove = httpsCallable(functions, "adminRemoveWaitlist");
      await remove({ classId, userId });
      await loadClassSummary();
      setWaitlistRows((current) => current.filter((row) => row.userId !== userId));
      pushNotice("Member removed from waitlist.", "success");
    } catch (err: any) {
      console.error("Remove waitlist error:", err);
      pushNotice(err?.message ?? "Failed to remove member from waitlist.", "error");
    } finally {
      setBusyWaitlistUserId(null);
    }
  }

  const progressPct = totalShown ? Math.round((checkedInShown / totalShown) * 100) : 0;
  const canBulkCheckIn = !loadingRoster && !bulkBusy && rows.some((r) => normalizeStatus(r) !== "checked_in");
  const canBulkUncheck = !loadingRoster && !bulkBusy && rows.some((r) => normalizeStatus(r) === "checked_in");
  const canBulkSelected = !loadingRoster && !bulkBusy && selectedIds.length > 0;

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <NoticeStack notices={notices} onDismiss={dismissNotice} />
      <UserTopNav />
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="rounded-3xl border border-neutral-800 bg-gradient-to-b from-neutral-950 to-black p-7 shadow-[0_0_40px_rgba(0,0,0,0.6)]">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <h1 className="font-heading uppercase text-white break-words leading-[0.9] text-4xl tracking-[0.25em] sm:text-6xl sm:tracking-widest">
                {classTitle}
              </h1>
              <div className="text-white/50 mt-2">{classMeta}</div>
            </div>

            <div className="shrink-0 text-right">
              <div className="text-xs uppercase tracking-widest text-white/40">Checked in</div>
              <div className="text-5xl font-bold">
                {checkedInShown}
                <span className="text-white/30">/{totalShown}</span>
              </div>
              {waitlistCount > 0 ? (
                <div className="mt-2 text-xs uppercase tracking-widest text-amber-200/80">
                  Waitlist {waitlistCount}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-5">
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-emerald-500/40 transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="mt-2 text-xs text-white/40 uppercase tracking-widest">{progressPct}% checked in</div>
          </div>
        </div>

        <div className="rounded-3xl border border-neutral-800 bg-neutral-950 p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="text-xl font-semibold">Attendees</div>

            <div className="flex flex-wrap gap-2 sm:ml-auto sm:items-center">
              <button
                onClick={() => {
                  setSelectMode((v) => !v);
                  clearSelected();
                }}
                disabled={loadingRoster || bulkBusy}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-semibold text-white/70 hover:bg-white/[0.06] disabled:text-white/40"
              >
                {selectMode ? "Done selecting" : "Select"}
              </button>

              <button
                onClick={checkInAll}
                disabled={!canBulkCheckIn}
                className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                  canBulkCheckIn
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/15"
                    : "border-white/10 bg-white/[0.03] text-white/40"
                }`}
              >
                {bulkBusy ? "Working…" : "Check in all"}
              </button>

              <button
                onClick={uncheckAll}
                disabled={!canBulkUncheck}
                className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                  canBulkUncheck
                    ? "border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/15"
                    : "border-white/10 bg-white/[0.03] text-white/40"
                }`}
              >
                {bulkBusy ? "Working…" : "Uncheck all"}
              </button>

              <button
                onClick={() => {
                  setAddMemberError("");
                  setSelectedUserId("");
                  setShowAddMemberModal(true);
                }}
                disabled={loadingRoster || bulkBusy}
                className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-500/15 disabled:text-white/40"
              >
                + Add member
              </button>

              <button
                onClick={loadRoster}
                disabled={loadingRoster || bulkBusy}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-semibold text-white/70 hover:bg-white/[0.06] disabled:text-white/40"
              >
                {loadingRoster ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>

          {selectMode && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="text-sm text-white/70">
                Selected: <span className="text-white font-semibold">{selectedIds.length}</span>
              </div>

              <div className="flex flex-wrap gap-2 sm:ml-auto">
                <button
                  onClick={() => bulkSetStatus("checked_in")}
                  disabled={!canBulkSelected}
                  className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                    canBulkSelected
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/15"
                      : "border-white/10 bg-white/[0.03] text-white/40"
                  }`}
                >
                  Check in
                </button>

                <button
                  onClick={() => bulkSetStatus("authorised_absence")}
                  disabled={!canBulkSelected}
                  className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                    canBulkSelected
                      ? "border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/15"
                      : "border-white/10 bg-white/[0.03] text-white/40"
                  }`}
                >
                  Authorised absence
                </button>

                <button
                  onClick={() => bulkSetStatus("dip")}
                  disabled={!canBulkSelected}
                  className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                    canBulkSelected
                      ? "border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/15"
                      : "border-white/10 bg-white/[0.03] text-white/40"
                  }`}
                >
                  Dip
                </button>

                <button
                  onClick={clearSelected}
                  disabled={loadingRoster || bulkBusy}
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-semibold text-white/70 hover:bg-white/[0.06] disabled:text-white/40"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {loadingRoster ? (
            <div className="mt-6 text-white/50">Loading roster…</div>
          ) : sortedRows.length === 0 ? (
            <div className="mt-6 text-white/60">No bookings yet.</div>
          ) : (
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sortedRows.map((r) => {
                const status = normalizeStatus(r);
                const isBusy = busyUserId === r.userId || bulkBusy;
                const isSelected = !!selected[r.userId];

                return (
                  <div
                    key={r.userId}
                    className={`
                      group rounded-2xl border p-5 text-left transition
                      ${status === "checked_in" ? "border-emerald-500/60 bg-emerald-500/10" : ""}
                      ${status === "dip" ? "border-red-500/60 bg-red-500/15 shadow-[0_0_20px_rgba(239,68,68,0.2)]" : ""}
                      ${status === "booked" ? "border-white/10 bg-white/[0.03]" : ""}
                      ${isBusy ? "opacity-60" : "hover:border-white/20 hover:bg-white/[0.05] hover:-translate-y-[1px]"}
                    `}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      {selectMode && (
                        <button
                          type="button"
                          onClick={() => toggleSelected(r.userId)}
                          className={`
                            shrink-0 h-6 w-6 rounded-md border flex items-center justify-center
                            ${isSelected ? "border-emerald-500/60 bg-emerald-500/15" : "border-white/15 bg-white/[0.03]"}
                          `}
                          aria-label="Select attendee"
                        >
                          {isSelected ? <span className="text-emerald-200 text-sm">✓</span> : null}
                        </button>
                      )}

                      <div
                        className={[
                          "shrink-0 rounded-full p-[2px] border",
                          status === "checked_in"
                            ? "border-emerald-500/70"
                            : status === "dip"
                            ? "border-red-500/60"
                            : "border-white/10",
                        ].join(" ")}
                      >
                        <UserAvatar name={r.name ?? "Member"} photoURL={r.photoURL} size={48} />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="truncate font-semibold text-white">{r.name ?? "Member"}</div>
                          {r.bookingSource === "payg" || r.guestTag ? (
                            <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-200">
                              Guest
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-white/50 truncate">{r.email ?? ""}</div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusPill status={status} />
                        {r.addedByAdmin && (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-amber-500/40 text-amber-200 bg-amber-500/10">
                            Admin add
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-white/30 font-mono">{r.checkedInAt ? "✓" : ""}</span>
                    </div>

                    {!selectMode && (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <button
                          onClick={() => setStatus(r.userId, status === "checked_in" ? "booked" : "checked_in")}
                          disabled={isBusy}
                          className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-white/70 hover:bg-white/[0.06] disabled:text-white/40"
                        >
                          {status === "checked_in" ? "Uncheck" : "Check in"}
                        </button>

                        <button
                          onClick={() => setStatus(r.userId, "authorised_absence")}
                          disabled={isBusy}
                          className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs font-semibold text-sky-200 hover:bg-sky-500/15 disabled:text-white/40"
                        >
                          Absence
                        </button>

                        <button
                          onClick={() => setStatus(r.userId, "dip")}
                          disabled={isBusy}
                          className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-200 hover:bg-red-500/15 disabled:text-white/40"
                        >
                          Dip
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-neutral-800 bg-neutral-950 p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="text-xl font-semibold">Waitlist</div>
            <div className="text-xs uppercase tracking-widest text-white/40">
              {waitlistRows.length} waiting
            </div>
          </div>

          {waitlistRows.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/55">
              Nobody is waiting for this class right now.
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {waitlistRows.map((row, index) => (
                <div
                  key={row.userId}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-500/30 bg-amber-500/10 text-xs font-semibold text-amber-200">
                      {index + 1}
                    </div>
                    <UserAvatar name={row.userName ?? "Member"} photoURL={row.photoURL} size={40} />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-white">{row.userName ?? "Member"}</div>
                      <div className="truncate text-sm text-white/45">
                        {row.email || formatJoinedTime(row.createdAt)}
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0 text-right">
                    <div className="text-xs uppercase tracking-widest text-amber-200/80">
                      Position {index + 1}
                    </div>
                    <div className="mt-1 text-xs text-white/45">
                      Joined {formatJoinedTime(row.createdAt)}
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => handlePromoteWaitlist(row.userId)}
                        disabled={busyWaitlistUserId === row.userId}
                        className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/15 disabled:text-white/40"
                      >
                        {busyWaitlistUserId === row.userId ? "Working..." : "Promote"}
                      </button>
                      <button
                        onClick={() => handleRemoveWaitlist(row.userId)}
                        disabled={busyWaitlistUserId === row.userId}
                        className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-200 hover:bg-red-500/15 disabled:text-white/40"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAddMemberModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-3xl border border-neutral-800 bg-neutral-950 p-6 shadow-[0_0_40px_rgba(0,0,0,0.6)]">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-white">Add member to class</h2>
              <p className="mt-1 text-sm text-white/50">
                This bypasses the usual booking deadline and adds them as an admin exception.
              </p>
            </div>

            <label className="mb-2 block text-sm font-semibold text-white/70">Select member</label>

            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={addingMember}
              className="w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white outline-none focus:border-white/20"
            >
              <option value="">Choose a member...</option>
              {addableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ?? u.email ?? u.id}
                </option>
              ))}
            </select>

            {addableUsers.length === 0 && (
              <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white/60">
                No additional members available to add.
              </div>
            )}

            {addMemberError ? (
              <div className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {addMemberError}
              </div>
            ) : null}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedUserId("");
                  setAddMemberError("");
                }}
                disabled={addingMember}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-semibold text-white/70 hover:bg-white/[0.06] disabled:text-white/40"
              >
                Cancel
              </button>

              <button
                onClick={handleAdminAddMember}
                disabled={addingMember || addableUsers.length === 0}
                className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-500/15 disabled:text-white/40"
              >
                {addingMember ? "Adding..." : "Add member"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
